// Phase 141 — Billing & plan panel inside /account.
//
// Surfaces the things a teacher actually needs when they ask "what
// plan am I on and how do I change it?":
//   • Current tier, billing period, next renewal / trial-end date
//   • "Cancels at period end" warning when applicable
//   • Manage-in-Stripe button (opens the Stripe Customer Portal where
//     they can update card, see invoices, cancel — the source of
//     truth for billing actions)
//   • Compact "other plans" row so they can switch without leaving
//     the page (Basic / Standard / Pro tiles with a Switch button
//     that creates a Checkout session)
//   • Link out to /pricing for the full comparison
//
// Backend already exposes everything via /api/billing/me + /tiers +
// /portal + /checkout — this panel is purely a UI assembly.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  CreditCard,
  ExternalLink,
  FileText,
  Loader2,
  Sparkles,
} from "lucide-react";
import {
  fetchInvoices,
  fetchMyBilling,
  fetchTiers,
  openBillingPortal,
  startCheckout,
} from "../lib/billing";

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-CA", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return null;
  }
};

const STATUS_COPY = {
  active: { label: "Active", tone: "ok" },
  trialing: { label: "Trial", tone: "info" },
  past_due: { label: "Past due", tone: "warn" },
  unpaid: { label: "Unpaid", tone: "warn" },
  canceled: { label: "Cancelled", tone: "muted" },
  incomplete: { label: "Setup pending", tone: "warn" },
  incomplete_expired: { label: "Setup expired", tone: "muted" },
};

const StatusBadge = ({ status, cancelAtPeriodEnd }) => {
  // Cancel-at-period-end is more important than the active status —
  // show it first so the teacher knows the plan ends soon even
  // though Stripe still reports `active`.
  if (cancelAtPeriodEnd) {
    return (
      <span
        data-testid="billing-status-badge"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-[0.15em] bg-[#fde7d2] text-[#7a4a1a]"
      >
        <AlertTriangle className="h-3 w-3" /> Cancels soon
      </span>
    );
  }
  const meta = STATUS_COPY[(status || "").toLowerCase()];
  if (!meta) {
    return (
      <span
        data-testid="billing-status-badge"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-[0.15em] bg-[#e8edf3] text-[#4a6984]"
      >
        Free
      </span>
    );
  }
  const tones = {
    ok: "bg-[#dff5e9] text-[#1f6b3a]",
    info: "bg-[#dbeefb] text-[#1d4a7a]",
    warn: "bg-[#fde0d8] text-[#9c2c2c]",
    muted: "bg-[#e8edf3] text-[#4a6984]",
  };
  return (
    <span
      data-testid="billing-status-badge"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-[0.15em] ${tones[meta.tone]}`}
    >
      {meta.label}
    </span>
  );
};

const formatPrice = (tier, period) => {
  if (!tier) return null;
  const monthly = tier.price_monthly_cad ?? 0;
  const yearly = tier.price_yearly_cad ?? 0;
  if (period === "yearly") {
    return `$${yearly} CAD / year`;
  }
  if (period === "monthly") {
    return `$${monthly} CAD / month`;
  }
  // Tier hasn't picked a period yet (e.g. free) — show monthly default.
  if (monthly === 0 && yearly === 0) return "Free";
  return `from $${monthly} CAD / month`;
};

const OtherPlanCard = ({ tier, currentTierId, switching, onSwitch }) => {
  const isCurrent = tier.id === currentTierId;
  const previewFeatures = (tier.features || []).slice(0, 3);
  return (
    <article
      data-testid={`billing-other-plan-${tier.id}`}
      className={`rounded-lg border p-4 bg-white flex flex-col gap-3 ${
        isCurrent
          ? "border-[#3EB489]/60 bg-[#3EB489]/5"
          : "border-[#4a6984]/15"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p
            className="font-extrabold uppercase tracking-[0.18em] text-[10px] text-[#4a6984]/75"
          >
            {tier.coming_soon ? tier.coming_soon_label || "Coming soon" : "Plan"}
          </p>
          <p
            data-testid={`billing-other-plan-name-${tier.id}`}
            className="mt-1 font-black text-lg text-[#1c2d3d] leading-tight"
          >
            {tier.name}
          </p>
        </div>
        {isCurrent && (
          <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#1f6b3a] bg-[#dff5e9] px-2 py-0.5 rounded-full">
            <Check className="h-3 w-3" /> Current
          </span>
        )}
      </div>
      <p
        data-testid={`billing-other-plan-price-${tier.id}`}
        className="font-extrabold text-[#1c2d3d] text-sm"
      >
        ${tier.price_monthly_cad} CAD
        <span className="text-[#4a6984]/70 font-semibold"> / month</span>
        {tier.price_yearly_cad ? (
          <span className="block text-[11px] text-[#4a6984]/75 font-semibold mt-0.5">
            or ${tier.price_yearly_cad}/yr
          </span>
        ) : null}
      </p>
      <ul className="space-y-1.5 text-[13px] text-[#4a6984] leading-snug flex-1">
        {previewFeatures.map((f, i) => (
          <li key={i} className="flex gap-1.5 items-start">
            <Check className="h-3.5 w-3.5 mt-0.5 text-[#3EB489] shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {isCurrent ? (
        <p className="text-[11px] text-[#4a6984]/65 font-medium">
          You&apos;re on this plan.
        </p>
      ) : tier.coming_soon ? (
        <p
          data-testid={`billing-other-plan-soon-${tier.id}`}
          className="text-[11px] text-[#4a6984]/75 font-semibold"
        >
          {tier.coming_soon_subline || "Coming soon."}
        </p>
      ) : (
        <button
          type="button"
          onClick={() => onSwitch(tier)}
          disabled={switching}
          data-testid={`billing-other-plan-switch-${tier.id}`}
          className="inline-flex items-center justify-center gap-2 h-9 px-3 rounded-md font-extrabold text-xs uppercase tracking-[0.12em] bg-[#1c2d3d] text-white hover:bg-[#0f1e2c] disabled:opacity-60 transition-colors"
        >
          {switching ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <>
              Switch to {tier.name}
              <ArrowRight className="h-3.5 w-3.5" />
            </>
          )}
        </button>
      )}
    </article>
  );
};

export default function AccountBillingPanel() {
  const [billing, setBilling] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [openingPortal, setOpeningPortal] = useState(false);
  const [switchingTierId, setSwitchingTierId] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const [me, list] = await Promise.all([fetchMyBilling(), fetchTiers()]);
        if (cancelled) return;
        setBilling(me);
        setTiers(list?.tiers || []);
      } catch (e) {
        if (cancelled) return;
        const detail = e?.response?.data?.detail || e?.message || "";
        setErr(String(detail) || "Couldn't load your billing details.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Lazy-load invoices only after we know the user has a Stripe
  // customer — saves a 200 round-trip + Stripe API call for free-tier
  // teachers who'd just see an empty list.
  useEffect(() => {
    if (!billing?.has_stripe_customer) {
      setInvoices([]);
      return;
    }
    let cancelled = false;
    setInvoicesLoading(true);
    fetchInvoices(3)
      .then((data) => {
        if (!cancelled) setInvoices(data?.invoices || []);
      })
      .catch(() => {
        // Non-fatal — the rest of the panel still works. Hide the
        // section silently rather than surface a Stripe error here.
        if (!cancelled) setInvoices([]);
      })
      .finally(() => {
        if (!cancelled) setInvoicesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [billing?.has_stripe_customer]);

  // Friendly "next event" line. Trial end wins (most actionable), then
  // renewal, then cancel scheduling.
  const nextEvent = useMemo(() => {
    if (!billing) return null;
    if (billing.trial_end) {
      return { label: "Trial ends", date: formatDate(billing.trial_end) };
    }
    if (billing.cancel_at_period_end && billing.current_period_end) {
      return {
        label: "Plan ends",
        date: formatDate(billing.current_period_end),
        warn: true,
      };
    }
    if (billing.current_period_end) {
      return { label: "Renews", date: formatDate(billing.current_period_end) };
    }
    return null;
  }, [billing]);

  const currentTier = useMemo(
    () => tiers.find((t) => t.id === billing?.tier_id) || null,
    [tiers, billing?.tier_id],
  );

  const onManage = async () => {
    setOpeningPortal(true);
    try {
      const { url } = await openBillingPortal();
      if (url) window.location.assign(url);
    } catch (e) {
      const detail =
        e?.response?.data?.detail || e?.message || "Couldn't open the billing portal.";
      toast.error("Billing portal unavailable", { description: String(detail) });
    } finally {
      setOpeningPortal(false);
    }
  };

  const onSwitch = async (tier) => {
    setSwitchingTierId(tier.id);
    try {
      // Default to monthly when switching from the panel — the
      // Checkout page itself lets them flip to yearly. Keeps this
      // panel simple; full price comparison still lives at /pricing.
      const { url } = await startCheckout({
        tier_id: tier.id,
        billing_period: billing?.period === "yearly" ? "yearly" : "monthly",
      });
      if (url) window.location.assign(url);
    } catch (e) {
      const detail =
        e?.response?.data?.detail || e?.message || "Couldn't start checkout.";
      toast.error("Checkout unavailable", { description: String(detail) });
    } finally {
      setSwitchingTierId("");
    }
  };

  if (loading) {
    return (
      <section
        data-testid="account-billing-panel"
        className="mt-6 ae-card p-6 sm:p-7 shadow-sm"
      >
        <div className="flex items-center gap-2 text-[#4a6984]">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm font-semibold">Loading billing…</span>
        </div>
      </section>
    );
  }

  if (err) {
    return (
      <section
        data-testid="account-billing-panel"
        className="mt-6 ae-card p-6 sm:p-7 shadow-sm"
      >
        <p
          data-testid="account-billing-error"
          className="text-sm text-[#a23a3a] font-semibold"
        >
          {err}
        </p>
      </section>
    );
  }

  const isFree = !billing?.is_active || billing?.tier_id === "free";

  return (
    <section
      data-testid="account-billing-panel"
      className="mt-6 ae-card p-6 sm:p-7 shadow-sm"
    >
      <header className="flex items-start justify-between gap-3">
        <div>
          <p className="font-bold uppercase text-xs tracking-[0.22em] text-[#4a6984]/70 inline-flex items-center gap-1.5">
            <CreditCard className="h-3.5 w-3.5" /> Billing &amp; plan
          </p>
          <h2
            data-testid="account-billing-heading"
            className="mt-2 font-black text-2xl text-[#1c2d3d] tracking-tight"
          >
            {billing?.tier_name || "Free"} plan
          </h2>
        </div>
        <StatusBadge
          status={billing?.status}
          cancelAtPeriodEnd={billing?.cancel_at_period_end}
        />
      </header>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div className="rounded-lg border border-[#4a6984]/10 p-3.5 bg-[#f4f5f7]/60">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/65">
            Price
          </p>
          <p
            data-testid="account-billing-price"
            className="mt-1 font-extrabold text-[#1c2d3d]"
          >
            {isFree
              ? "Free"
              : currentTier
              ? formatPrice(currentTier, billing?.period)
              : "—"}
          </p>
          {billing?.period && (
            <p className="text-[11px] text-[#4a6984]/75 font-semibold mt-0.5 capitalize">
              {billing.period} billing
            </p>
          )}
        </div>
        <div className="rounded-lg border border-[#4a6984]/10 p-3.5 bg-[#f4f5f7]/60">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/65">
            {nextEvent?.label || "Next renewal"}
          </p>
          <p
            data-testid="account-billing-next-event"
            className={`mt-1 font-extrabold ${
              nextEvent?.warn ? "text-[#9c2c2c]" : "text-[#1c2d3d]"
            }`}
          >
            {nextEvent?.date || (isFree ? "No upcoming charge" : "—")}
          </p>
          {billing?.cancel_at_period_end && (
            <p className="text-[11px] text-[#9c2c2c]/85 font-semibold mt-0.5">
              You can re-enable any time in Stripe.
            </p>
          )}
        </div>
      </div>

      <div className="mt-5 flex flex-col sm:flex-row gap-2 flex-wrap">
        {billing?.has_stripe_customer && (
          <button
            type="button"
            onClick={onManage}
            disabled={openingPortal}
            data-testid="account-billing-manage-btn"
            className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md font-extrabold text-sm bg-[#1c2d3d] text-white hover:bg-[#0f1e2c] disabled:opacity-60 transition-colors"
          >
            {openingPortal ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Opening&hellip;
              </>
            ) : (
              <>
                Manage billing in Stripe
                <ExternalLink className="h-3.5 w-3.5 opacity-80" />
              </>
            )}
          </button>
        )}
        <Link
          to="/pricing"
          data-testid="account-billing-compare-link"
          className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md font-extrabold text-sm border-2 border-[#1c2d3d] bg-white text-[#1c2d3d] hover:bg-[#f4f5f7] transition-colors"
        >
          <Sparkles className="h-4 w-4" />
          See full pricing comparison
        </Link>
      </div>

      {/* Recent invoices — Phase 141.B. Pulls last 3 from
          stripe.Invoice.list so teachers can grab a receipt for
          school-board reimbursement without a portal round-trip.
          Hidden when there's no Stripe customer (free tier). */}
      {billing?.has_stripe_customer && (invoicesLoading || invoices.length > 0) && (
        <div className="mt-7" data-testid="account-billing-invoices">
          <p className="font-bold uppercase text-xs tracking-[0.22em] text-[#4a6984]/70 inline-flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" /> Recent invoices
          </p>
          <ul className="mt-3 divide-y divide-[#4a6984]/10 rounded-lg border border-[#4a6984]/15 bg-white overflow-hidden">
            {invoicesLoading && invoices.length === 0 && (
              <li className="flex items-center gap-2 px-4 py-3 text-sm text-[#4a6984]">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading invoices…
              </li>
            )}
            {invoices.map((inv) => {
              const amountCents = inv.amount_paid || inv.amount_due || 0;
              const amount = (amountCents / 100).toFixed(2);
              const date = formatDate(inv.created);
              const statusMeta = {
                paid: { label: "Paid", className: "bg-[#dff5e9] text-[#1f6b3a]" },
                open: { label: "Open", className: "bg-[#dbeefb] text-[#1d4a7a]" },
                void: { label: "Void", className: "bg-[#e8edf3] text-[#4a6984]" },
                uncollectible: {
                  label: "Uncollectible",
                  className: "bg-[#fde0d8] text-[#9c2c2c]",
                },
                draft: { label: "Draft", className: "bg-[#e8edf3] text-[#4a6984]" },
              }[(inv.status || "").toLowerCase()] || {
                label: inv.status || "—",
                className: "bg-[#e8edf3] text-[#4a6984]",
              };
              return (
                <li
                  key={inv.id}
                  data-testid={`account-billing-invoice-${inv.id}`}
                  className="flex flex-wrap items-center gap-3 px-4 py-3"
                >
                  <div className="flex-1 min-w-[160px]">
                    <p className="font-extrabold text-sm text-[#1c2d3d]">
                      ${amount} {inv.currency}
                    </p>
                    <p className="text-[12px] text-[#4a6984]/80 font-medium">
                      {date || "—"}
                      {inv.number ? ` · ${inv.number}` : ""}
                    </p>
                  </div>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-[0.15em] ${statusMeta.className}`}
                  >
                    {statusMeta.label}
                  </span>
                  <div className="flex items-center gap-2">
                    {inv.invoice_pdf && (
                      <a
                        href={inv.invoice_pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`account-billing-invoice-pdf-${inv.id}`}
                        className="text-xs font-extrabold text-[#1c2d3d] hover:underline inline-flex items-center gap-1"
                      >
                        PDF
                        <ExternalLink className="h-3 w-3 opacity-70" />
                      </a>
                    )}
                    {inv.hosted_invoice_url && (
                      <a
                        href={inv.hosted_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`account-billing-invoice-view-${inv.id}`}
                        className="text-xs font-extrabold text-[#1c2d3d] hover:underline inline-flex items-center gap-1"
                      >
                        View
                        <ExternalLink className="h-3 w-3 opacity-70" />
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Other plans — compact tiles so a teacher can switch
          without leaving the page. The full comparison still lives
          at /pricing for the deeper feature-by-feature table. */}
      {tiers && tiers.length > 0 && (
        <div className="mt-7" data-testid="account-billing-other-plans">
          <p className="font-bold uppercase text-xs tracking-[0.22em] text-[#4a6984]/70">
            All plans
          </p>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            {tiers.map((t) => (
              <OtherPlanCard
                key={t.id}
                tier={t}
                currentTierId={billing?.tier_id}
                switching={switchingTierId === t.id}
                onSwitch={onSwitch}
              />
            ))}
          </div>
        </div>
      )}

      <p className="mt-5 text-[11px] text-[#4a6984]/65 font-medium leading-relaxed">
        Card details, invoices, and cancellation are handled securely by Stripe.
        We never store your payment information on Atomic Edu.
      </p>
    </section>
  );
}
